/**
 * Steady game clock for GM-driven games.
 *
 * The server stays authoritative and tick-based: every queued action is one
 * deterministic server step. The page only decides *when* steps happen, on a
 * fixed wall clock, independent of network latency:
 *
 *   - startFixedClock fires onTick every intervalMs against absolute
 *     deadlines (no drift from slow callbacks or responses);
 *   - createTickBatcher queues the actions and, whenever no request is in
 *     flight, sends everything queued (up to maxBatch) as one request, so
 *     ticks that elapse during a slow round trip go out with the next one.
 */

/**
 * @param {object} opts
 * @param {(actions: string[]) => Promise<any>} opts.send
 * @param {number} opts.maxBatch  server-side cap per request
 * @param {number} [opts.maxQueued] backlog bound; beyond it new ticks are refused
 * @param {(data: any, actions: string[]) => void} [opts.onResult]
 * @param {(err: any, actions: string[]) => void} [opts.onError]
 * @param {(action: string, info: { pending: number, limit: number }) => void} [opts.onDrop]
 */
export function createTickBatcher({ send, maxBatch, maxQueued, onResult, onError, onDrop }) {
    const limit = maxQueued ?? maxBatch * 4;
    let queue = [];
    let inFlight = false;
    let stopped = false;

    const flush = async () => {
        if (inFlight || stopped || queue.length === 0) return;
        const batch = queue.splice(0, maxBatch);
        inFlight = true;
        try {
            const data = await send(batch);
            if (onResult) onResult(data, batch);
        } catch (err) {
            if (onError) onError(err, batch);
        } finally {
            inFlight = false;
        }
        flush();
    };

    return {
        push(action) {
            if (stopped) return false;
            if (queue.length >= limit) {
                // Surface the drop so games can warn / count lost input
                // instead of silently discarding ticks past the backlog.
                if (onDrop) onDrop(action, { pending: queue.length, limit });
                return false;
            }
            queue.push(action);
            flush();
            return true;
        },
        /** Stop sending; an in-flight response is still delivered. */
        stop() {
            stopped = true;
            queue = [];
        },
        get pending() { return queue.length; },
        get inFlight() { return inFlight; },
    };
}

/**
 * Call onTick on a fixed wall clock. intervalMs may be a function (read
 * before each tick, e.g. Tetris gravity by level). If the timer fires late
 * (background tab, busy main thread) up to maxCatchUp missed ticks are
 * delivered, then the clock resyncs. Returns a stop function.
 */
export function startFixedClock({ intervalMs, onTick, maxCatchUp = 4, now = () => Date.now() }) {
    const interval = typeof intervalMs === 'function' ? intervalMs : () => intervalMs;
    let next = now() + interval();
    let timer = null;
    let stopped = false;

    const run = () => {
        if (stopped) return;
        let fired = 0;
        while (!stopped && now() >= next && fired <= maxCatchUp) {
            onTick();
            fired += 1;
            next += interval();
        }
        if (now() >= next) next = now() + interval(); // too far behind: resync
        if (!stopped) timer = setTimeout(run, Math.max(0, next - now()));
    };
    timer = setTimeout(run, Math.max(0, next - now()));
    return () => {
        stopped = true;
        if (timer) clearTimeout(timer);
    };
}
